import { Constants } from 'src/shared/utilities/constants.util';
import { FunctionUtil } from 'src/shared/utilities/function.util';
import { PageLayoutService } from 'src/shared/services/page-layout.service';
import { RouteUtil } from 'src/shared/utilities/route.util';

export abstract class HuronEntityAdminPage {
    readonly initialLoadingSection = "initial";
    readonly adminActionLoadingSection = "adminAction";
    readonly adminListLoadingSection = "adminList";
    readonly mobileLoadingSection = "mobile";
    
    readonly itemsPerPage = 50;
    readonly scrollDistance = 1;
	readonly scrollThrottle = 300;
    readonly scrollSelector = '.adminListScroll';

    private errorMessage: string;
    private warningMessage: string;

    addButtonEnabled = false;        
    adminActionButton1LabelFunction: Function;
    adminActionButton1LabelText = "";
    adminActionButton2LabelFunction: Function;
    adminActionButton2LabelText = "";
    adminActionLabelText = "";
    adminActionLabelFunction: Function;
    adminActionLabelMaxLength = 50;
    adminActionLoading = false;
    adminListLoading = false;
    confirmModalActive = false;
    deleteButtonEnabled = false;
    huronAdmin = false;
    initialLoading = false;    
    mobileLoading = false;
    offset = 0;    
    readOnly = false;
    saveButtonEnabled = false;
    searchText = '';
    selectedFilter = '';
    selection: any;
    showAdminActionWindow = false;
    isCostCenterSaved = false;
    sortAscending = true;
    sortTypes: string[] = [];    

	debounceSearch: Function = FunctionUtil.DelayFunctionExecution(() => { this.refreshAdminList(false, false); }, 500);

    constructor(private _pageLayoutService: PageLayoutService)  { }

    protected abstract adminActionSave(): Promise<any>;
    protected abstract adminListAppend(): Promise<any>;
    protected abstract adminActionRefresh(): Promise<any>;
    protected abstract adminListRefresh(newFirst: boolean): Promise<any>;

    adminListSelection(items: any[], maintainSelection: boolean) {        
        if (items && items.length > 0) {
            let found = this.selection && items.some(g => g.id == this.selection.id);
            let hasSelection = this.selection != undefined && this.selection != null;

            if (!hasSelection || (hasSelection && !found) || (found && !maintainSelection)) {
                this.selection = {...items[0]};                
            }            
        }
        else {
            this.selection = null;
        }
    }

    async appendAdminList() {
		let loadingSections = [ this.adminListLoadingSection, this.mobileLoadingSection ];
		this.setLoading(loadingSections, true);

        await this.adminListAppend();
        
		this.setLoading(loadingSections, false);
	}

    onChangeSortOrder() {
        this.sortAscending = !this.sortAscending;
        this.refreshAdminList(false, false);
    }
    
    checkCrudPermissions(page: string) {
        let createPermissions = sessionStorage.getItem(Constants.CreatePermissionAreaName);
        let deletePermissions = sessionStorage.getItem(Constants.DeletePermissionAreaName);
        let updatePermissions = sessionStorage.getItem(Constants.UpdatePermissionAreaName);

        this.addButtonEnabled = RouteUtil.isPermissionAreaAvailable(createPermissions, page);
        this.deleteButtonEnabled = RouteUtil.isPermissionAreaAvailable(deletePermissions, page);
        this.saveButtonEnabled = RouteUtil.isPermissionAreaAvailable(updatePermissions, page);

        this.readOnly = !this.addButtonEnabled && !this.saveButtonEnabled;
    }

    checkLoadingStatus(section: string): boolean {
        switch (section) {
            case this.initialLoadingSection:
                return this.initialLoading;
            case this.adminListLoadingSection:
                return this.adminListLoading;
            case this.mobileLoadingSection:
                return this.mobileLoading;
            case this.adminActionLoadingSection:
                return this.adminActionLoading;
            default:
                return false;
        }
    }

    cleanUp() {
        this._pageLayoutService.clearLayoutStyle();
    }

    getErrorMessage(): string {
        return this.errorMessage;
    }

    getSortOrderString(): string {
		const arrowDirection = (this.sortAscending) ? Constants.DirectionAscending : Constants.DirectionDescending;
        return arrowDirection;
    }
    
    getWarningMessage(): string {
        return this.warningMessage;
    }

    async initialize(
        page: string,
        sorts: string[],
        adminActionLabel?: () => string,
        adminActionButton1Label?: () => string,
        adminActionButton2Label?: () => string) {
        this._pageLayoutService.setLayoutStyle('entity-admin');

        let loadingSections = [ this.initialLoadingSection, this.mobileLoadingSection ];
        this.setLoading(loadingSections, true);

        this.checkCrudPermissions(page);
      
        this.showAdminActionWindow = true;
        this.sortTypes = sorts;
        this.selectedFilter = this.sortTypes[0];

        this.adminActionButton1LabelFunction = adminActionButton1Label;
        this.adminActionButton2LabelFunction = adminActionButton2Label;
        this.adminActionLabelFunction = adminActionLabel;

        await this.refreshAdminList(false, false);

        this.setLoading(loadingSections, false);
    }

    limitAdminActionLabel() {
        if (!this.selection || (this.adminActionLabelFunction == undefined || this.adminActionLabelFunction == null)) {
            return;
        }
        let originalAdminActionLabelMaxLength = this.adminActionLabelMaxLength;

        this.adminActionLabelText = this.adminActionLabelFunction();

        if (this.adminActionButton1LabelFunction != undefined && this.adminActionButton1LabelFunction != null) {
            this.adminActionButton1LabelText = this.adminActionButton1LabelFunction();
            this.adminActionLabelMaxLength -= this.adminActionButton1LabelText.length;
        }
        
        if (this.adminActionButton2LabelFunction != undefined && this.adminActionButton2LabelFunction != null) {
            this.adminActionButton2LabelText = this.adminActionButton2LabelFunction();
            this.adminActionLabelMaxLength -= this.adminActionButton2LabelText.length;
        }        

        if (this.adminActionLabelMaxLength < 0) {
            this.adminActionLabelMaxLength = 0;
        }
        
        if (this.adminActionLabelText.length <= this.adminActionLabelMaxLength) {
            this.adminActionLabelMaxLength = originalAdminActionLabelMaxLength;
            return;
        }        
        
        this.adminActionLabelText = this.adminActionLabelText.substring(0, this.adminActionLabelMaxLength);
        this.adminActionLabelText += "…";
        this.adminActionLabelMaxLength = originalAdminActionLabelMaxLength;
    }

    onCancel() {
        this.showAdminActionWindow = false;
        this.refreshAdminList(false, true);
    }

    async onSearchKey(event: any) {
        // Wait the specified milliseconds to get the full input before doing the actual search.
		this.debounceSearch();
    }

    async onSave(): Promise<any> {
        let loadingSections = [ this.adminListLoadingSection, this.mobileLoadingSection, this.adminActionLoadingSection ];
        this.setLoading(loadingSections, !this.initialLoading);

        let result = await this.adminActionSave();

        this.setLoading(loadingSections, false);
        this.isCostCenterSaved = true;
        return result;
    }

    onScrollDown() {
		this.offset += this.itemsPerPage;
		this.appendAdminList();
	}

    onSelection(selected: any) {
        this.showAdminActionWindow = true;
        this.selection = {...selected};
        this.refreshAdminAction();
    }

    onSortTypesDropDownChanged(event: any) {
		this.selectedFilter = event.target.value;
		this.refreshAdminList(false, false);
	}

    async refreshAdminAction(): Promise<any> {
        let loadingSections = [ this.mobileLoadingSection, this.adminActionLoadingSection ];
        this.setLoading(loadingSections, !this.initialLoading);

        let result = await this.adminActionRefresh();
        
        this.limitAdminActionLabel();

        this.setLoading(loadingSections, false);

        return result;
    }

    async refreshAdminList(newFirst: boolean, maintainSelection: boolean): Promise<boolean> {
        this.offset = 0;
        
        let loadingSections = [ this.adminListLoadingSection, this.mobileLoadingSection ];
        this.setLoading(loadingSections, !this.initialLoading);

        let results = await this.adminListRefresh(newFirst);

        if (results) {
            this.adminListSelection(results, maintainSelection)            
        }

        await this.refreshAdminAction();

        this.setLoading(loadingSections, false);

        return results != undefined && results != null;
    }

    setErrorMessage(message: string) {
        this.errorMessage = message;
        this.warningMessage = '';
    }    

    setLoading(sections: string[], active: boolean) {
		sections.forEach((section) => {
			switch (section) {				
				case this.initialLoadingSection:
						this.initialLoading = active;
                break;	
                case this.adminListLoadingSection:
						this.adminListLoading = active;
    			break;
                case this.adminActionLoadingSection:
						this.adminActionLoading = active;
				break;
			}
		});		
    }

    setSelectedFilter(event: any) {
		this.selectedFilter = event.target.value;
		this.selection = null;
    }
    
    setWarningMessage(message: string) {
        this.errorMessage = '';
        this.warningMessage = message;
    }
    
    sortTable(listName: string, columnIndex: number, startingRowNumber: number) {
        var table, rows, switching, shouldSwitch, dir, i, switchCount = 0;
        table = (document.getElementById(listName)) as HTMLTableElement;
        switching = true;
        dir = Constants.DirectionAscending;

        while(switching) {
            switching = false;
            rows = table.rows;

            for (i = startingRowNumber; i < (rows.length - 1); i++) {
                shouldSwitch = false;

                let x = rows[i].getElementsByTagName("TD")[columnIndex].innerHTML.toLowerCase();
                let y = rows[i + 1].getElementsByTagName("TD")[columnIndex].innerHTML.toLowerCase();
                
                shouldSwitch = this.compareKeys(x,y,dir);
                if(shouldSwitch)
                  break;
                             
            }  
            
            if (shouldSwitch) {
                rows[i].parentNode.insertBefore(rows[i + 1], rows[i]);
                switching = true;
                switchCount++;
            }
            else {
                if (switchCount == 0 && dir == Constants.DirectionAscending) {
                    dir = Constants.DirectionDescending;
                    switching = true;
                }
            }
        }
    }

    compareKeys(firstValue,secondValue,sortDirection):boolean
    {
      var swap: boolean;
      let numLeft = Number(firstValue);
      let numRight = Number(secondValue);

      // all numbers are samller than anything with letters so check if the values are numeric
      if(!isNaN(numLeft))
      {
        if(isNaN(numRight))
          swap = false;
        else
          swap = numLeft > numRight;
      }
      else
      {
        if(!isNaN(numRight))
          swap = true;
        else
          swap = firstValue > secondValue;
      }

      //reverse sort if ascending
      if(sortDirection === Constants.DirectionAscending)
      {
        return !swap;
      }

      return swap;
    }

}